import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { PageContainer, PageHeader, StatCard } from "@/components/Page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Download, FileText, Filter, X, Check, ChevronDown, RefreshCw } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend, PieChart, Pie, Cell } from "recharts";

const STATUS_COLORS = {
  pending: "#EAB308",
  ongoing: "#3B82F6",
  on_review: "#6366F1",
  complete: "#16A34A",
  failed: "#DC2626",
};

function MultiSelect({ label, options, selected, onChange, testid, placeholder }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(query.toLowerCase()));
  const toggle = (id) => {
    if (selected.includes(id)) onChange(selected.filter((x) => x !== id));
    else onChange([...selected, id]);
  };
  const selectedLabels = options.filter((o) => selected.includes(o.id)).map((o) => o.label);

  return (
    <div>
      <Label className="label-eyebrow">{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={testid}
            className="w-full mt-2 rounded-none border border-zinc-300 h-10 px-3 text-left text-sm bg-white flex items-center justify-between hover:border-black transition-colors"
          >
            <span className={selected.length ? "text-black" : "text-zinc-400"}>
              {selected.length === 0
                ? placeholder
                : selected.length === 1
                  ? selectedLabels[0]
                  : `${selected.length} selected`}
            </span>
            <ChevronDown className="h-3.5 w-3.5 text-zinc-500" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0 rounded-none border-zinc-300" align="start">
          <div className="p-2 border-b border-zinc-200">
            <Input
              placeholder="Search…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-8 rounded-none border-zinc-200 focus-visible:ring-0 focus-visible:border-black text-xs"
              data-testid={`${testid}-search`}
            />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 && <div className="p-3 text-xs text-zinc-500">No matches.</div>}
            {filtered.map((o) => {
              const active = selected.includes(o.id);
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => toggle(o.id)}
                  data-testid={`${testid}-option-${o.id}`}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-zinc-100 ${active ? "bg-zinc-50" : ""}`}
                >
                  <div className={`h-3.5 w-3.5 border ${active ? "bg-black border-black" : "border-zinc-400"} flex items-center justify-center`}>
                    {active && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                  </div>
                  <span className="truncate">{o.label}</span>
                </button>
              );
            })}
          </div>
          {selected.length > 0 && (
            <div className="p-2 border-t border-zinc-200 flex items-center justify-between">
              <span className="text-xs text-zinc-500">{selected.length} selected</span>
              <button
                type="button"
                onClick={() => onChange([])}
                data-testid={`${testid}-clear`}
                className="text-xs uppercase tracking-wider font-semibold underline underline-offset-2"
              >
                Clear
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>
      {selectedLabels.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {selectedLabels.slice(0, 4).map((n, i) => (
            <span key={i} className="inline-flex items-center gap-1 border border-zinc-300 px-2 py-0.5 text-[10px] font-medium bg-white">
              {n}
              <button type="button" onClick={() => toggle(options.find((o) => o.label === n).id)} className="hover:text-red-600">
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          ))}
          {selectedLabels.length > 4 && (
            <span className="text-[10px] text-zinc-500 self-center">+{selectedLabels.length - 4} more</span>
          )}
        </div>
      )}
    </div>
  );
}

export default function Reports() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [estates, setEstates] = useState([]);
  const [residents, setResidents] = useState([]);

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [estateIds, setEstateIds] = useState([]);
  const [residentIds, setResidentIds] = useState([]);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    if (estateIds.length) p.set("estate_ids", estateIds.join(","));
    if (residentIds.length) p.set("resident_ids", residentIds.join(","));
    return p;
  }, [dateFrom, dateTo, estateIds, residentIds]);

  const activeFilterCount = (dateFrom ? 1 : 0) + (dateTo ? 1 : 0) + (estateIds.length ? 1 : 0) + (residentIds.length ? 1 : 0);

  useEffect(() => {
    (async () => {
      try {
        const [e, r] = await Promise.all([
          api.get("/estates"),
          api.get("/users", { params: { role: "resident" } }),
        ]);
        setEstates(e.data);
        setResidents(r.data);
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);

  const loadSummary = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/summary?${params.toString()}`);
      setSummary(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [params]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const resetFilters = () => {
    setDateFrom(""); setDateTo(""); setEstateIds([]); setResidentIds([]);
  };

  const statusData = summary ? Object.entries(summary.status_counts).map(([k, v]) => ({ name: k, value: v })) : [];
  const categoryData = summary ? Object.entries(summary.category_counts).map(([k, v]) => ({ name: k, value: v })) : [];
  const estateData = summary?.per_estate || [];

  const csvUrl = `${API_BASE}/reports/tickets.csv?${params.toString()}`;
  const pdfUrl = `${API_BASE}/reports/tickets.pdf?${params.toString()}`;

  return (
    <AppShell>
      <PageContainer>
        <PageHeader
          eyebrow="Super Admin"
          title="Reports"
          description="Filter by date range, estate, or resident and export as CSV or PDF."
          actions={
            <div className="flex items-center gap-2">
              <a
                href={csvUrl}
                data-testid="download-csv-btn"
                className="inline-flex items-center gap-2 border border-zinc-300 px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-black hover:text-white hover:border-black transition-colors"
              >
                <Download className="h-3.5 w-3.5" /> Export CSV
              </a>
              <a
                href={pdfUrl}
                data-testid="download-pdf-btn"
                className="inline-flex items-center gap-2 border border-zinc-300 bg-black text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-zinc-800 hover:border-black transition-colors"
              >
                <FileText className="h-3.5 w-3.5" /> Export PDF
              </a>
            </div>
          }
        />

        {/* Filter panel */}
        <div className="bg-white border border-zinc-200 p-4 sm:p-6 mb-8" data-testid="reports-filter-panel">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              <div className="label-eyebrow">Filters</div>
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold bg-black text-white uppercase tracking-wider" data-testid="active-filter-count">
                  {activeFilterCount} active
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={resetFilters}
                data-testid="reset-filters-btn"
                className="inline-flex items-center gap-1 text-xs uppercase tracking-wider font-semibold text-zinc-600 hover:text-black"
              >
                <RefreshCw className="h-3 w-3" /> Reset
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label className="label-eyebrow">Date From</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="filter-date-from"
                className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black mt-2 h-10"
              />
            </div>
            <div>
              <Label className="label-eyebrow">Date To</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="filter-date-to"
                className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black mt-2 h-10"
              />
            </div>
            <MultiSelect
              label="Estates"
              placeholder="All estates"
              options={estates.map((e) => ({ id: e.id, label: e.name }))}
              selected={estateIds}
              onChange={setEstateIds}
              testid="filter-estates"
            />
            <MultiSelect
              label="Residents"
              placeholder="All residents"
              options={residents.map((r) => ({ id: r.id, label: `${r.name} (${r.email})` }))}
              selected={residentIds}
              onChange={setResidentIds}
              testid="filter-residents"
            />
          </div>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          <StatCard label="Estates" value={summary?.totals.estates ?? "—"} testid="stat-estates" hint="Total network" />
          <StatCard label="Managers" value={summary?.totals.managers ?? "—"} testid="stat-managers" hint="Total network" />
          <StatCard label="Residents" value={summary?.totals.residents ?? "—"} testid="stat-residents" hint="Total network" />
          <StatCard label="Units" value={summary?.totals.units ?? "—"} testid="stat-units" hint="Total network" />
          <StatCard label="Tickets" value={summary?.totals.tickets ?? "—"} testid="stat-tickets" hint={activeFilterCount ? "In selection" : "All time"} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white border border-zinc-200 p-6" data-testid="chart-status">
            <div className="label-eyebrow mb-4">Tickets by Status</div>
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={statusData} dataKey="value" nameKey="name" outerRadius={100} label>
                    {statusData.map((entry) => (
                      <Cell key={entry.name} fill={STATUS_COLORS[entry.name] || "#71717A"} />
                    ))}
                  </Pie>
                  <Tooltip /><Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-zinc-200 p-6" data-testid="chart-category">
            <div className="label-eyebrow mb-4">Tickets by Category</div>
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={categoryData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#000000" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="bg-white border border-zinc-200 p-6" data-testid="chart-per-estate">
          <div className="label-eyebrow mb-4">Tickets per Estate</div>
          {estateData.length === 0 ? (
            <div className="text-sm text-zinc-500">No estates match the current filter.</div>
          ) : (
            <div style={{ width: "100%", height: 300 }}>
              <ResponsiveContainer>
                <BarChart data={estateData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip /><Legend />
                  <Bar dataKey="tickets" fill="#000000" name="Total" />
                  <Bar dataKey="open" fill="#3B82F6" name="Open" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {loading && <div className="fixed bottom-6 right-6 bg-black text-white px-4 py-2 text-xs uppercase tracking-wider font-semibold shadow-lg" data-testid="reports-loading">Loading…</div>}
      </PageContainer>
    </AppShell>
  );
}
